import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Mail, Phone, Lock, MapPin, Briefcase, Gift, Sparkles, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { categoriesAPI, citiesAPI, providersAPI, authAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import Footer from '../components/Footer';

const RegisterProviderPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setAuth } = useAuth();
  const [verificationStep, setVerificationStep] = useState('form'); // 'form', 'verify', 'complete'
  const [verificationCode, setVerificationCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [categories, setCategories] = useState([]);
  const [cities, setCities] = useState([]);
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', password: '', city: '', category: '', bio: '', price: '',
  });

  useEffect(() => {
    categoriesAPI.getAll().then(res => setCategories(res.data)).catch(() => {});
    citiesAPI.getAll().then(res => setCities(res.data)).catch(() => {});
  }, []);

  const handleChange = (field) => (e) => {
    setFormData({ ...formData, [field]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (verificationStep === 'form') {
      // Step 1: Send verification code
      setSubmitting(true);
      try {
        await authAPI.sendVerification({
          email: formData.email,
          name: formData.name
        });
        setVerificationStep('verify');
      } catch (err) {
        setError(err.response?.data?.detail || t('common.error'));
      } finally {
        setSubmitting(false);
      }
    } else if (verificationStep === 'verify') {
      // Step 2: Verify code and register
      setSubmitting(true);
      try {
        // Verify code
        await authAPI.verifyCode({
          email: formData.email,
          code: verificationCode
        });
        
        // Register provider
        const res = await providersAPI.register(formData);
        if (res.data.user && res.data.token) {
          setAuth(res.data.user, res.data.token);
        } else {
          localStorage.setItem('khadam_token', res.data.token);
        }
        setVerificationStep('complete');
        
        setTimeout(() => {
          navigate('/dashboard');
        }, 2500);
      } catch (err) {
        setError(err.response?.data?.detail || t('common.error'));
      } finally {
        setSubmitting(false);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Back button */}
        <button
          onClick={() => navigate('/')}
          data-testid="register-back-btn"
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-8 transition-colors"
        >
          <ArrowLeft size={20} />
          <span className="text-sm font-medium">{t('register.backHome')}</span>
        </button>

        {/* Success State */}
        {verificationStep === 'complete' && (
          <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check size={40} className="text-green-600" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('register.success')} 🎉</h2>
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white px-6 py-3 rounded-full shadow-lg mb-4">
              <Gift size={20} />
              <span className="font-bold">{t('register.successTrial')}</span>
            </div>
            <p className="text-gray-600 mb-2">{t('register.successMessage')}</p>
            <p className="text-sm text-gray-500">{t('register.redirecting')}</p>
          </div>
        )}

        {/* Form States */}
        {verificationStep !== 'complete' && (
          <>
            {/* Header with Free Trial Promo */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white px-6 py-3 rounded-full shadow-lg mb-4 animate-pulse">
                <Gift size={24} />
                <span className="font-bold text-lg">{t('register.trialOffer')}</span>
                <Sparkles size={20} />
              </div>
              <h1 className="text-4xl font-bold text-gray-900 mb-3">
                {verificationStep === 'form' ? t('register.title') : t('register.verificationTitle')}
              </h1>
              <p className="text-gray-600 max-w-2xl mx-auto">
                {verificationStep === 'form' 
                  ? t('register.subtitle')
                  : `${t('register.verificationSent')} ${formData.email}`
                }
              </p>
            </div>

            <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 md:p-10">
              {verificationStep === 'form' ? (
                <div className="space-y-6">
                  {/* Free Trial Info */}
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl p-6 mb-6">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                        <Gift size={24} className="text-white" />
                      </div>
                      <div>
                        <h3 className="font-bold text-green-900 text-lg mb-2">🎁 {t('register.trialInfo')}</h3>
                        <ul className="space-y-2 text-sm text-green-800">
                          <li className="flex items-center gap-2">
                            <Check size={16} className="text-green-600" />
                            <span>{t('register.trial3Months')}</span>
                          </li>
                          <li className="flex items-center gap-2">
                            <Check size={16} className="text-green-600" />
                            <span>{t('register.noPrepayment')}</span>
                          </li>
                          <li className="flex items-center gap-2">
                            <Check size={16} className="text-green-600" />
                            <span>{t('register.testFree')}</span>
                          </li>
                        </ul>
                        <p className="text-xs text-green-700 mt-3">
                          {t('register.afterTrial')}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Form Fields */}
                  <div className="grid grid-cols-1 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">{t('register.fullName')} *</label>
                      <div className="relative">
                        <User size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                          type="text"
                          value={formData.name}
                          onChange={handleChange('name')}
                          placeholder={t('register.fullName')}
                          data-testid="register-name-input"
                          className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          required
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">{t('register.email')} *</label>
                        <div className="relative">
                          <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                          <input
                            type="email"
                            value={formData.email}
                            onChange={handleChange('email')}
                            placeholder="email@exemple.com"
                            data-testid="register-email-input"
                            className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            required
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">{t('register.phone')} *</label>
                        <div className="relative">
                          <Phone size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                          <input
                            type="tel"
                            value={formData.phone}
                            onChange={handleChange('phone')}
                            placeholder="+212 6XX XXX XXX"
                            data-testid="register-phone-input"
                            className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            required
                          />
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">{t('register.password')} *</label>
                      <div className="relative">
                        <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                          type="password"
                          value={formData.password}
                          onChange={handleChange('password')}
                          placeholder="••••••••"
                          data-testid="register-password-input"
                          className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          required
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">{t('register.city')} *</label>
                        <div className="relative">
                          <MapPin size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                          <select
                            value={formData.city}
                            onChange={handleChange('city')}
                            data-testid="register-city-select"
                            className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white"
                            required
                          >
                            <option value="">{t('register.selectCity')}</option>
                            {cities.map((c) => (
                              <option key={c.id} value={c.name}>{c.name}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">{t('register.category')} *</label>
                        <div className="relative">
                          <Briefcase size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                          <select
                            value={formData.category}
                            onChange={handleChange('category')}
                            data-testid="register-category-select"
                            className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white"
                            required
                          >
                            <option value="">{t('register.selectCategory')}</option>
                            {categories.map((c) => (
                              <option key={c.id} value={c.id}>{t(`categoryNames.${c.id}`, c.name)}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">{t('register.price')} *</label>
                      <input
                        type="number"
                        value={formData.price}
                        onChange={handleChange('price')}
                        placeholder="Ex: 150"
                        min="50"
                        data-testid="register-price-input"
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      />
                      <p className="text-xs text-gray-500 mt-1">{t('register.priceHint')}</p>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">{t('register.bio')}</label>
                      <textarea
                        value={formData.bio}
                        onChange={handleChange('bio')}
                        placeholder={t('register.bioPlaceholder')}
                        data-testid="register-bio-input"
                        rows={4}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                      />
                    </div>
                  </div>

                  {error && (
                    <div className="p-4 bg-red-50 border-2 border-red-200 rounded-xl text-sm text-red-600">
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="register-submit-btn"
                    className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-bold rounded-xl py-4 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg text-base"
                  >
                    {submitting ? t('register.submitting') : t('register.submit')}
                  </button>

                  <p className="text-xs text-center text-gray-500">
                    {t('register.terms')}
                  </p>
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Mail size={32} className="text-blue-600" />
                    </div>
                    <p className="text-gray-600 text-sm">
                      {t('register.verificationSent')}<br />
                      <strong>{formData.email}</strong>
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2 text-center">{t('register.verificationCode')}</label>
                    <input
                      type="text"
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value)}
                      placeholder="123456"
                      maxLength={6}
                      data-testid="register-code-input"
                      className="w-full text-center text-3xl tracking-widest font-bold py-4 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-2 text-center">{t('register.verificationCode')}</p>
                  </div>

                  {error && (
                    <div className="p-4 bg-red-50 border-2 border-red-200 rounded-xl text-sm text-red-600">
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={submitting || verificationCode.length !== 6}
                    data-testid="register-verify-btn"
                    className="w-full bg-blue-500 hover:bg-blue-600 text-white font-bold rounded-xl py-4 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submitting ? t('register.verifying') : t('register.verifyAndRegister')}
                  </button>

                  <button
                    type="button"
                    onClick={() => setVerificationStep('form')}
                    className="w-full text-sm text-gray-500 hover:text-gray-700 py-2"
                  >
                    ← {t('register.back')}
                  </button>
                </div>
              )}
            </form>
          </>
        )}
      </div>
      
      <Footer />
    </div>
  );
};

export default RegisterProviderPage;
